// src/pages/SupplierDetail.js
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { getSupplierById } from '../services/supplierService';
import BackButton from '../components/BackButton';
import formatDateToStandard, { DATE_FORMAT_DISPLAY, formatDateTimeToStandard } from '../utils/DateFormatter';

const SupplierDetail = () => {
  const { orgId, id } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [supplier, setSupplier] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    getSupplierById(orgId, id)
      .then(setSupplier)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [orgId, id]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const mainFields = {
    'Supplier ID': supplier?.id,
    'Name': supplier?.supplier_name,
    'Email': supplier?.email,
    'Phone': supplier?.contact_number,
    [`Date Modified (${DATE_FORMAT_DISPLAY})`]: supplier ? formatDateToStandard(supplier.date_modified) : '',
  };

  const additionalFields = { ...supplier };
  Object.keys(mainFields).forEach((key) => {
    delete additionalFields[key];
  });
  delete additionalFields.meta;
  delete additionalFields.audit_log;

  function parseNestedFields(obj, prefix = '') {
    const result = [];
    Object.entries(obj).forEach(([key, value]) => {
      if (value === null) {
        return;
      }
      if (prefix === 'Organization' && !['id', 'organization_name'].includes(key)) {
        return;
      }
      if (prefix === 'Organization' && /^(meta|date_modified|date_created)$/i.test(key)) {
        return;
      }
      const displayKey = (prefix
        ? `${prefix} ${key.replace(/_/g, ' ')}`
        : key.replace(/_/g, ' '))
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result.push(...parseNestedFields(value, displayKey));
      } else {
        let displayValue = value;
        if (typeof value === 'string' && !isNaN(Date.parse(value))) {
          displayValue = formatDateTimeToStandard(value);
        }
        result.push([displayKey, displayValue]);
      }
    });
    return result;
  }

  return (
    <Paper sx={{ p: 3, bgcolor: 'background.paper', color: 'text.primary', backdropFilter: 'saturate(140%) blur(6px)', border: '1px solid', borderColor: 'divider' }}>
      <BackButton />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error ? (
            <Typography variant="h6" color="error">{error}</Typography>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                {supplier.supplier_name}
              </Typography>

              <Grid container spacing={2}>
                {Object.entries(mainFields).map(([key, value]) => (
                  <React.Fragment key={key}>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {key}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      {key === `Date Modified (${DATE_FORMAT_DISPLAY})` ? (
                        <Tooltip title={`Format: ${DATE_FORMAT_DISPLAY}`}>
                          <span>{value}</span>
                        </Tooltip>
                      ) : (
                        <Typography variant="body1">{value}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Tooltip title="Copy">
                        <IconButton onClick={() => handleCopy(value)}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>

              {Object.keys(additionalFields).length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>
                    Detailed Information
                  </Typography>
                  <TableContainer component={Paper} sx={{ mt: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Copy</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parseNestedFields(additionalFields).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell>{key}</TableCell>
                            <TableCell>
                              {Array.isArray(value) ? value.join(', ') : (value !== null && value !== undefined ? value.toString() : 'N/A')}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Copy">
                                <IconButton onClick={() => handleCopy(value)}>
                                  <ContentCopy />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </>
      )}
    </Paper>
  );
};

export default SupplierDetail;
